import { Auth } from 'aws-amplify'
import isEmpty from 'lodash.isempty'

import { getThriveAuthConfig } from '@api/platform'
import { amplifyConfiguration } from './amplifyConfiguration'
import { signIn } from './sign-in'
import { ApiError } from '@util/api-error'
import { SUBMISSION_ERR } from '@enums/form-submission-error'
import { SignUpFormValues } from '@util/cognito-forms'

const EMAIL_REGEX = /^(\S+)@(\S+)/
const DOMAIN_INDEX = 2

// `cognitoAction` associated with `SignUpForm`
export async function signUp(
  signUpFormValues: SignUpFormValues
): Promise<void> {
  const { email, password, companyHint, firstName, lastName } = signUpFormValues

  const {
    emailDomains,
    cognitoUserPoolId,
    cognitoUserPoolClientId,
  } = await getThriveAuthConfig({ companyHint })

  // prevent calls to the Cognito sign-up method when the user's email domain
  // isn't whitelisted. downstream this will trigger a user error display
  if (!isRegisteredEmailDomain(email, emailDomains)) {
    throw new ApiError({
      code: 'EMAIL_DOMAIN_INVALID',
      message: SUBMISSION_ERR.EMAIL_DOMAIN_INVALID,
    })
  }

  // after collecting the required the Cognito Ids from Thrive platform,
  // configure the Amplify library
  if (!amplifyConfiguration.isConfigured) {
    amplifyConfiguration.configure(cognitoUserPoolId, cognitoUserPoolClientId)
  }

  // go through Cognito's formal sign-up flow — bypass for Cypress
  if (!window.Cypress) {
    const formattedVals = {
      username: email,
      password,
      attributes: {
        name: firstName,
        family_name: lastName,
        'custom:company_name': companyHint,
        email: email,
      },
    }

    await Auth.signUp(formattedVals)
  }

  /**
   * a call to Amplify.Auth.signIn() is required to complete user authorization,
   * otherwise associated Amplify auth methods don't see the user as auth'd.
   * additionally, the platform `users/initialize` call will throw a 401.
   *
   * more than likely this is due to us not using a Cognito verification step
   */
  await signIn(signUpFormValues)
}

/**
 * email domain check is required for a Platform `domainKey`. the check is
 * bypassed for companies with legacy cohort codes — email domains array is
 * empty for co's with legacy cohort codes.
 */
function isRegisteredEmailDomain(email: string, emailDomains: Array<string>) {
  const emailMatch = email.match(EMAIL_REGEX)
  const domain = emailMatch ? emailMatch[DOMAIN_INDEX] : ''
  const hasDomain = emailDomains.includes(domain)
  const isLegacy = isEmpty(emailDomains)

  return hasDomain || isLegacy
}
